import { QueryFilter } from '@/types';

/**
 * Maps attack surface selection values to indexed boolean Neo4j field filters (ENG-1862).
 *
 * Replaces the old scalar-in-array pattern (`$val IN n.attackSurface`) with RANGE-indexed
 * boolean field queries (`WHERE n.isExternal = true`) for NodeIndexSeek performance.
 *
 * @param surfaces - Array of selected surface values ('external', 'internal', 'cloud', 'repository')
 * @returns Array of QueryFilter objects for the selected surfaces
 */
export function mapAttackSurfaceToFilters(surfaces: string[]): QueryFilter[] {
  return surfaces
    .map((surface): QueryFilter | null => {
      switch (surface) {
        case 'external':
          return { field: 'isExternal', operator: '=', value: true };
        case 'internal':
          return { field: 'isInternal', operator: '=', value: true };
        case 'cloud':
          return { field: 'isCloud', operator: '=', value: true };
        case 'repository':
          return { field: 'class', operator: '=', value: 'repository' };
        default:
          return null;
      }
    })
    .filter((f): f is QueryFilter => f !== null);
}

/**
 * Builds a single QueryFilter (or OR-grouped filter) for attack surface selections.
 *
 * @param surfaces - Array of selected surface values
 * @returns A single QueryFilter, OR-grouped QueryFilter, or null if no surfaces selected
 */
export function buildAttackSurfaceFilter(
  surfaces: string[]
): QueryFilter | null {
  const filters = mapAttackSurfaceToFilters(surfaces);
  if (filters.length === 0) return null;
  if (filters.length === 1) return filters[0];
  return { field: '', operator: 'OR', value: filters };
}
